const post = {
  slug: 'why-nominees-still-need-to-be-told',
  title: 'Why a nominee on paper is not enough',
  excerpt:
    'Banks, EPFO, and insurers will not call your family. If the people you named never learn that the account exists, nomination becomes a silent file—and that is how unclaimed money grows.',
  date: '2026-08-27',
  updated: '2026-08-27',
  category: 'Family',
  featured: false,
  coverImage: '/blog/why-nominees-still-need-to-be-told/cover.jpg',
  coverAlt: 'A passbook left in a drawer—nomination on file, but nobody in the house knows to look.',
  keywords:
    'unclaimed money India, nominee not informed, family does not know assets, EPF unclaimed, forgotten FD, KeepMyAsset',
  seoTitle: 'Why a Nominee on Paper Is Not Enough',
  seoDescription:
    'Nomination tells the bank who may claim. It does not tell your family the asset exists. Learn why nominees still need to be informed—and how to close that gap.',
  related: ['what-is-a-nominee', 'how-to-add-a-nominee-in-india'],
  faqs: [
    {
      q: 'Will the bank inform my nominee after I die?',
      a: 'Usually no. The bank acts when someone approaches them with a death certificate and a claim. If your family does not know the branch, the account, or even that you had an FD, the nomination sits unused.',
    },
    {
      q: 'Should I share account passwords with my nominee?',
      a: 'No. Passwords and OTPs are for you while you are alive. Share the existence of assets, the institution names, and who is nominated—not login credentials. KeepMyAsset is built around verified guidance, not handing over access.',
    },
  ],
  body: `
People finish the nomination form and feel finished. The name is on the account. The duty feels discharged. Then years pass. The passbook goes in a drawer. The LIC premium is auto-debited. The EPF login is forgotten.

When something happens, the nomination is still on the institution’s server. Your family is standing in a house full of papers, trying to guess which banks you used.

## Institutions do not look for your family

A bank’s job is to safeguard deposits and follow the contract. It is not to run a missing-persons search for your children. EPFO will not visit your address because a UAN went silent. An insurer may send a lapse letter to an old email. That is not the same as sitting your nominee down and explaining what to do.

So the system only works if two things are true at once: a nominee is on file, and a living person knows to walk into that office with a death certificate.

![Unopened envelopes and a forgotten passbook: the nomination can exist and still never be used.](/blog/why-nominees-still-need-to-be-told/forgotten-records.jpg)

## Why families still lose the asset

- The only record was an SMS from 2018 on a phone that was reset.
- The FD was opened in a hometown branch nobody visits anymore.
- EPF was left with a first employer. The family never had the UAN.
- Insurance was bought at an airport counter. The policy PDF is in a dead inbox.
- Someone was nominated, but they were never told—because it felt awkward, or “too soon.”

Each of those is ordinary. Together they explain a country-sized pile of unclaimed money. The nomination form did its job. Discovery failed.

## Tell them without handing over the keys

You do not need to share IFSC codes at dinner. You do need one trusted adult to know: I have accounts, I have named nominees, and here is how you start if I am not here.

Write the map. Institution, asset type, nominee name. Keep documents where they can be found. Do not bury the only copy in a locker whose key is also a secret.

:::callout What KeepMyAsset is for
We give you one secure place to record assets and nominees, and a verified way to inform the right people when a family request is approved. We do not transfer money, open claims, or replace the legal nomination at the bank.
:::

Nomination is the lock the institution understands. Telling your family is the key they are holding. You need both.
`,
};

export default post;
